/* global __DEV__ */

import dynamic from 'next/dynamic';

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  fetchDepthData,
  fetchTradesData,
  clearDepthData,
  clearTradesData,
  setDataIsLoading,
} from '../actions/load';
import { TrafficHolder } from '../utils/scheduler';

import {
  depthSocketConnected,
  depthSocketDisconnected,
  socketReceivedDepth,
  socketReceivedTrade,
  socketReceivedTickers,
  socketReceivedBatch as socketReceivedBatchAction,
  socketReceivedMarginUpdate as socketReceivedMarginUpdateAction,
} from '../actions/websocket';
import { formatNumber } from '../utils/number';

const AbstractSocketProvider = dynamic(
  import('./AbstractSocketProvider'), {
    ssr: false,
    loading: () => null,
  }
);

const TICKER_FREQUENCY = '3000ms';
const WS_PATH = '/stream?streams=';

const mapStateToProps = ({
  isLoggedIn, trades, books,
  locale,
  metaData,
  tickSizes,
  listenKey,
}, { symbol }) => ({
  isLoggedIn,
  trades: trades[symbol],
  books: books[symbol],
  locale,
  tickSize: tickSizes[symbol] || 8,
  symbol,
  title: metaData?.title,
  listenKey,
});

const mapDispatchToProps = {
  fetchDepthData,
  fetchTradesData,
  socketConnected: depthSocketConnected,
  socketDisconnected: depthSocketDisconnected,
  setDataIsLoading,
  socketReceivedBatch: socketReceivedBatchAction,
  socketReceivedMarginUpdate: socketReceivedMarginUpdateAction,
  socketReceivedDepth,
  socketReceivedTrade,
  socketReceivedTickers,
};

const {
  func,
} = PropTypes;

export default
@connect(mapStateToProps, mapDispatchToProps)
class TradingDataProvider extends Component {
  _connects = 0;

  _tradesHolder = new TrafficHolder(this.props.socketReceivedTrade, {
    onPause: () => this._setDataIsLoading('trades', true),
    onResume: () => this._setDataIsLoading('trades', false),
  }).pause();

  _depthHolder = new TrafficHolder(this.props.socketReceivedDepth, {
    onPause: () => this._setDataIsLoading('depth', true),
    onResume: () => this._setDataIsLoading('depth', false),
  }).pause();

  static propTypes = {
    isLoggedIn: PropTypes.bool,
    symbol: PropTypes.string,
    wssUri: PropTypes.string.isRequired,
    trades: PropTypes.array,
    fetchDepthData: func.isRequired,
    fetchTradesData: func.isRequired,
    shouldRefresh: PropTypes.bool,
    children: PropTypes.node,
    pingStream: func,
    socketConnected: func,
    socketDisconnected: func,
    socketReceivedBatch: func,
    socketReceivedMarginUpdate: func,
    books: PropTypes.object,
    locale: PropTypes.string,
    tickSize: PropTypes.number,
    title: PropTypes.string,
    listenKey: PropTypes.string,
    setDataIsLoading: func,
    socketReceivedTrade: func,
    socketReceivedDepth: func,
    socketReceivedTickers: func,
  };

  static defaultProps = {
    trades: [],
    shouldRefresh: false,
    children: null,
    symbol: null,
    books: {},
    socketConnected: () => {},
    socketDisconnected: () => {},
    locale: 'en',
  }

  price = ''

  componentDidMount() {
    this._fetch();
  }

  shouldComponentUpdate({
    trades, books, symbol, isLoggedIn, listenKey,
  }) {
    if (symbol !== this.props.symbol) return true;
    if (isLoggedIn !== this.props.isLoggedIn || listenKey !== this.props.listenKey) {
      return this.props.isLoggedIn ? listenKey !== undefined : true;
    }
    const { trades: oldTrades, books: oldBooks } = this.props;
    if (!trades) return true;
    const { __lastBatchSize__: lastBatchSize } = trades;
    let shouldRefresh = false;
    if (oldTrades && oldTrades.length &&
        trades && trades.length &&
        oldTrades[0].a < trades[0].a - lastBatchSize) {
      // old trades - refresh market data.
      shouldRefresh = true;
    }

    if (oldBooks && books) {
      if (typeof books.firstUpdateId !== 'undefined' &&
          typeof books.lastUpdateId !== 'undefined' &&
          books.firstUpdateId > oldBooks.lastUpdateId + 1) {
        // this.state.lastDepthFirstUpdateId !== books.firstUpdateId) {
        // TODO: never setState in shouldComponentUpdate
        // this.setState({ lastDepthFirstUpdateId: books.firstUpdateId });
        // if hase loss package refresh depth
        shouldRefresh = true;

        if (__DEV__) {
          console.warn(
            `Refreshing books fetching ;oldBooks lastUpdateId=${oldBooks.lastUpdateId
            }   and new data get firstUpdateID =${books.firstUpdateId}`);
        }
      }
    }

    if (books && books.asks && books.bids) {
      let arr = Object.keys(books.asks);

      const asksMin = Math.min.apply(null, arr);

      arr = Object.keys(books.bids);
      const bidsMax = Math.max.apply(null, arr);

      if (!Number.isNaN(asksMin) && !Number.isNaN(bidsMax) &&
          asksMin <= bidsMax && !this._depthHolder.isPaused()) {
        // shouldRefresh = true;
        return true;
      }
    }
    if (shouldRefresh &&
        !this.props.shouldRefresh &&
        this._connects > 1) {
      return true;
    }
    return false;
  }

  componentDidUpdate() {
    this._fetch();
  }

  componentWillUnmount() {
    const { symbol } = this.props;
    // save memory
    clearDepthData(symbol);
    clearTradesData(symbol);
  }

  _handleDocumentTitle=price => {
    const { title } = this.props;
    document.title = `${price} | ${title} `;
  }

  _onMessage = ({ data, stream }) => {
    const {
      listenKey, socketReceivedBatch, symbol,
    } = this.props;
    if (stream === listenKey) socketReceivedBatch([data]);
    const _symbol = stream.split('@')[0].toUpperCase();

    if (Array.isArray(data)) {
      this.props.socketReceivedTickers(data);
    } else if (data.e === 'aggTrade' && _symbol === symbol) {
      if (data?.p) {
        const { tickSize } = this.props;
        const price = formatNumber(data.p, tickSize);
        if (this.price !== price) this._handleDocumentTitle(price); // update document.title
      }
      data.symbol = _symbol;
      this._tradesHolder.receive(data);
    } else if (data.e === 'depthUpdate' && _symbol === symbol) {
      data.symbol = _symbol;
      this._depthHolder.receive(data);
    }
  };

  _onReconnect = () => {
    this._fetch();
  }

  _onConnected = () => {
    ++this._connects;
    this.props.socketConnected();
  }

  _setDataIsLoading(key, isLoading) {
    this.props.setDataIsLoading({ key, isLoading });
  }

  _fetch() {
    const {
      symbol,
    } = this.props;

    if (symbol) {
      this._depthHolder.pause();
      this.props.fetchDepthData(symbol)
        .then(() => {
          this._depthHolder.resume();
        })
        .catch(() => {
          this._depthHolder.resume();
        });
      this._tradesHolder.pause();
      this.props.fetchTradesData(symbol)
        .then(() => {
          this._tradesHolder.resume();
        })
        .catch(() => {
          this._tradesHolder.resume();
        });

    // https://www.binance.com/en/trade/BCC_BTC
    } else {
      this._depthHolder.resume();
      this._tradesHolder.resume();
    }
  }

  render() {
    const {
      symbol,
      wssUri,
      socketDisconnected,
      listenKey,
      isLoggedIn,
    } = this.props;

    const names = symbol
      ? [
        `!miniTicker@arr@${TICKER_FREQUENCY}`,
        `${symbol.toLowerCase()}@depth.b10`,
        `${symbol.toLowerCase()}@aggTrade.b10`,
        listenKey,
      ].filter((n) => !!n)
      : [
        `!miniTicker@arr@${TICKER_FREQUENCY}`,
      ];

    const url = wssUri.replace(/\/ws$/, WS_PATH) + names.join('/');

    return (
      <Fragment>
        {isLoggedIn === false || (isLoggedIn && listenKey !== undefined) ?
          <AbstractSocketProvider
            //url="wss://stream.binance.com/stream?streams=!miniTicker@arr@3000ms"
            url={url}
            keepAlive={true}
            onConnected={this._onConnected}
            onReconnect={this._onReconnect}
            onDisconnected={socketDisconnected}
            onMessage={this._onMessage}
          />
          : null}
        {this.props.children || null}
      </Fragment>
    );
  }
}
